import { useEffect, useMemo, useState } from 'react';
import type { DateSegmentProps, DateValue } from 'react-aria-components';
import { DateRangePicker as AriaDateRangePicker } from 'react-aria-components';
import { fromDate, getLocalTimeZone } from '@internationalized/date';
import {
  CalendarCell,
  CalendarGrid,
  DateInput,
  DateSegment,
  Dialog,
  Group,
  Heading,
  IconButton,
  Input,
  Menu,
  MenuItem,
  MenuSection,
  Popover,
  RangeCalendar,
  TextField,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import moment from 'moment';

import { Box } from '../../Box/Box';
import { LiveModeIndicator } from '../../LiveModeIndicator/LiveModeIndicator';
import { Stack } from '../../Stack/Stack';
import { Text } from '../../Text/Text';

import { parseDateRangeInput } from './parseDateRangeInput';
import type { DateRangePreset, DateRangeValue } from './types';
import { getPresetLabel, presetLabel, presetStartDate, presetValue } from './utils';

type DateRange = {
  startDate: Date;
  endDate: Date;
};

type Props = {
  selectedValue: DateRangeValue;
  onDatesChange: (startDate: Date, endDate: Date, preset?: DateRangePreset) => void;
  onLiveModeChange?: (isLiveMode: boolean) => void;
  presets: DateRangePreset[];
  minDate: Date;
  maxDate?: Date;
  noCustom?: boolean;
  showLiveMode?: boolean;
  liveModePaused?: boolean;
  disabled?: boolean;
  showTextInput?: boolean;
};

function getDateValuesFromDates(value: DateRange): { start: DateValue; end: DateValue } {
  return {
    start: fromDate(value?.startDate, getLocalTimeZone()),
    end: fromDate(value?.endDate, getLocalTimeZone()),
  };
}

function getDatesFromDateValues(value: { start: DateValue; end: DateValue }): DateRange {
  return { startDate: value.start.toDate(getLocalTimeZone()), endDate: value.end.toDate(getLocalTimeZone()) };
}

function startOfDay(date: Date): Date {
  return moment(date).startOf('day').toDate();
}

function endOfDay(date: Date): Date {
  return moment(date).endOf('day').toDate();
}

export const DateRangePickerV2: React.FC<Props> = ({
  selectedValue,
  onDatesChange,
  onLiveModeChange,
  presets,
  minDate,
  maxDate = new Date(),
  noCustom = false,
  showLiveMode = false,
  liveModePaused = false,
  disabled = false,
  showTextInput = true,
}) => {
  const [displayPresets, setDisplayPresets] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [typedInput, setTypedInput] = useState('');
  const [typedInputIsInvalid, setTypedInputIsInvalid] = useState(false);

  // Create a DateRange from selectedValue
  const dateRange: DateRange | null = useMemo(() => {
    if (!selectedValue.startDate || !selectedValue.endDate) {
      return null;
    }
    return {
      startDate: selectedValue.startDate,
      endDate: selectedValue.endDate,
    };
  }, [selectedValue.startDate, selectedValue.endDate]);

  const [rangeValue, setRangeValue] = useState<DateRange | null>(dateRange);

  // Sync internal state with external prop changes (e.g., from histogram drag selection)
  useEffect(() => {
    if (dateRange) {
      setRangeValue(dateRange);
    }
  }, [dateRange]);

  // Determine selected preset
  const selectedPreset = useMemo(() => {
    if (selectedValue.selectedPreset) {
      return presets.find((preset) => presetValue(preset) === presetValue(selectedValue.selectedPreset!));
    }
    return undefined;
  }, [selectedValue.selectedPreset, presets]);

  const handleLiveModeToggle = () => {
    const newLiveModeState = !selectedValue.isLiveMode;

    // When live mode is enabled, set 15-minute preset as default range
    if (newLiveModeState) {
      const fifteenMinutePreset: DateRangePreset = { unit: 'minutes', quantity: 15 };
      const endDate = moment().toDate();
      const startDate = presetStartDate(fifteenMinutePreset);
      setRangeValue({ startDate, endDate });
      onDatesChange(startDate, endDate, fifteenMinutePreset);
    }

    if (onLiveModeChange) {
      onLiveModeChange(newLiveModeState);
    }

    togglePopover(false);
  };

  const handleSelectPreset = (preset?: DateRangePreset) => {
    if (!preset) {
      return;
    }

    const range: DateRange = {
      startDate: presetStartDate(preset),
      endDate: moment().toDate(),
    };

    // When a preset is selected, disable live mode
    if (selectedValue.isLiveMode && onLiveModeChange) {
      onLiveModeChange(false);
    }

    setRangeValue(range);
    onDatesChange(range.startDate, range.endDate, preset);
    togglePopover(false);
  };

  // Parses free text like "last 7 days" or "Jan 1 - Jan 7" and applies it as
  // either a relative preset or an absolute range. Surfaces an invalid state
  // when the text can't be understood.
  const handleTypedInputSubmit = () => {
    const parsed = parseDateRangeInput(typedInput);
    if (!parsed) {
      setTypedInputIsInvalid(true);
      return;
    }

    setTypedInputIsInvalid(false);

    // A relative phrase that matches one of the *available* preset buttons (e.g.
    // "last 7 days") is applied as that preset so it stays relative and
    // highlights in the menu. We match against `availablePresets` rather than
    // all `presets` so a phrase naming a preset that's hidden from the menu
    // (out of the [minDate, maxDate] window) falls through to the clamped
    // custom-range path below instead of committing an out-of-bounds preset.
    //
    // A relative phrase that isn't an available preset (e.g. "last 2 days") is
    // resolved to concrete dates and applied as a custom range below. Emitting
    // it as a preset would round-trip through a preset value that upstream
    // consumers can't resolve, so they fall back to the default preset —
    // leaving the picker showing the previous window instead of the typed range.
    if (parsed.type === 'preset') {
      const knownPreset = availablePresets.find((preset) => presetValue(preset) === presetValue(parsed.preset));
      if (knownPreset) {
        handleSelectPreset(knownPreset);
        return;
      }
    }

    // Resolve the parsed input to a concrete [start, end] range. Relative
    // phrases anchor the end at "now"; absolute ranges use the parsed dates.
    const { startDate: rawStartDate, endDate: rawEndDate } =
      parsed.type === 'preset'
        ? { startDate: presetStartDate(parsed.preset), endDate: moment().toDate() }
        : { startDate: parsed.startDate, endDate: parsed.endDate };

    // Clamp both ends into the picker's allowed window. Clamping each end
    // independently (rather than only raising the start / lowering the end)
    // keeps a fully out-of-window range from inverting into start > end.
    const clampToBounds = (date: Date): Date => {
      if (minDate && date < minDate) {
        return minDate;
      }
      if (maxDate && date > maxDate) {
        return maxDate;
      }
      return date;
    };
    const startDate = clampToBounds(rawStartDate);
    const endDate = clampToBounds(rawEndDate);

    // A range that sat entirely outside [minDate, maxDate] collapses to a single
    // bound after clamping; treat that as invalid rather than committing an
    // empty/degenerate range.
    if (startDate >= endDate) {
      setTypedInputIsInvalid(true);
      return;
    }

    if (selectedValue.isLiveMode && onLiveModeChange) {
      onLiveModeChange(false);
    }

    setRangeValue({ startDate, endDate });
    onDatesChange(startDate, endDate, undefined);
    togglePopover(false);
  };

  const handleCalendarChange = (range: { start: DateValue; end: DateValue } | null) => {
    const dates = range && getDatesFromDateValues(range);

    if (dates) {
      // Set start to beginning of day and end to end of day (only for calendar selections)
      dates.startDate = startOfDay(dates.startDate);
      dates.endDate = endOfDay(dates.endDate);

      // set to the exact same date and time as minDate
      if (minDate && dates.startDate < minDate) {
        dates.startDate = minDate;
      }

      // set to the exact same date and time as maxDate
      if (maxDate && dates.endDate > maxDate) {
        dates.endDate = maxDate;
      }

      onDatesChange(dates.startDate, dates.endDate, undefined);

      if (onLiveModeChange) {
        onLiveModeChange(false);
      }
    }

    setRangeValue(dates);
    togglePopover(false);
  };

  const handleDateRangePickerChange = (range: { start: DateValue; end: DateValue } | null) => {
    const dates = range && getDatesFromDateValues(range);

    if (dates) {
      // Validate against min/max but preserve the time the user entered
      if (minDate && dates.startDate < minDate) {
        dates.startDate = minDate;
      }

      if (maxDate && dates.endDate > maxDate) {
        dates.endDate = maxDate;
      }

      onDatesChange(dates.startDate, dates.endDate, undefined);

      if (onLiveModeChange) {
        onLiveModeChange(false);
      }
    }

    setRangeValue(dates);
    togglePopover(false);
  };

  const handleCancelCalendar = () => {
    setDisplayPresets(true);
    setIsOpen(false);
    setTypedInput('');
    setTypedInputIsInvalid(false);
  };

  const togglePopover = (open: boolean) => {
    if (!open) {
      handleCancelCalendar();
    }
    setIsOpen(open);
  };

  const valueAsDateValue = rangeValue ? getDateValuesFromDates(rangeValue) : undefined;

  const minDateCalendarValue = minDate && fromDate(startOfDay(minDate), getLocalTimeZone());
  const maxDateCalendarValue = maxDate && fromDate(endOfDay(maxDate), getLocalTimeZone());

  const availablePresets = presets.filter((preset) => {
    const startDate = presetStartDate(preset);
    const endDate = moment().toDate();
    if (
      (minDate && startOfDay(startDate) < startOfDay(minDate)) ||
      (maxDate && startOfDay(endDate) > startOfDay(maxDate))
    ) {
      return false;
    }
    return true;
  });

  const showYear = useMemo(() => {
    if (selectedValue.selectedPreset) {
      return false;
    }

    return selectedValue.startDate.getFullYear() !== selectedValue.endDate.getFullYear();
  }, [selectedValue.selectedPreset, selectedValue.startDate, selectedValue.endDate]);

  const renderDateSegment = (segment: DateSegmentProps['segment']) => {
    if (segment.type === 'month') {
      return (
        <>
          <DateSegment segment={segment} />
          <DateSegment
            segment={{ type: 'literal', text: '/', isPlaceholder: false, placeholder: '', isEditable: false }}
          />
        </>
      );
    }

    if (segment.type === 'day') {
      return (
        <>
          <DateSegment segment={segment} />
        </>
      );
    }

    if (segment.type === 'year' && showYear) {
      return (
        <>
          <DateSegment
            segment={{ type: 'literal', text: '/', isPlaceholder: false, placeholder: '', isEditable: false }}
          />
          <DateSegment segment={segment} />
        </>
      );
    }

    if (segment.type === 'hour') {
      return (
        <>
          <DateSegment
            segment={{ type: 'literal', text: ', ', isPlaceholder: false, placeholder: '', isEditable: false }}
          />
          <DateSegment segment={segment} />
          <DateSegment
            segment={{ type: 'literal', text: ':', isPlaceholder: false, placeholder: '', isEditable: false }}
          />
        </>
      );
    }

    if (segment.type === 'minute') {
      return (
        <>
          <DateSegment segment={segment} />
        </>
      );
    }

    if (segment.type === 'dayPeriod') {
      segment.text = segment.text.toLowerCase();
      return <DateSegment segment={segment} />;
    }

    return <></>;
  };

  return (
    <AriaDateRangePicker
      value={valueAsDateValue}
      onChange={handleDateRangePickerChange}
      aria-label="select custom or preset date range"
      granularity="minute"
      minValue={minDateCalendarValue}
      maxValue={maxDateCalendarValue}
      isDisabled={disabled}
      hideTimeZone
    >
      <Group
        onClick={() => togglePopover(true)}
        id="date-range-picker-group"
        style={{ justifyContent: 'space-between', whiteSpace: 'pre' }}
      >
        <Stack direction="row" align="center" gap="8" alignItems="center">
          {selectedValue.isLiveMode ? (
            <Stack direction="row" align="center" gap="4" alignItems="center">
              <LiveModeIndicator paused={liveModePaused} />
              <Text>Live</Text>
            </Stack>
          ) : (
            <>
              <DateTag>{getPresetLabel(selectedValue.startDate, selectedValue.endDate)}</DateTag>
              {selectedPreset ? (
                <>{presetLabel(selectedPreset)}</>
              ) : (
                <Stack direction="row" align="center" gap="3" alignItems="center">
                  <DateInput slot="start">{renderDateSegment}</DateInput>
                  <span aria-hidden="true">-</span>
                  <DateInput slot="end">{renderDateSegment}</DateInput>
                </Stack>
              )}
            </>
          )}
        </Stack>

        <IconButton
          onPress={() => togglePopover(true)}
          aria-label="open date range picker"
          icon={isOpen ? 'chevron-up' : 'chevron-down'}
          variant="minimal"
          size="small"
        />
      </Group>
      <Popover isOpen={isOpen} onOpenChange={togglePopover} placement="bottom right" style={{ padding: 0 }}>
        <Dialog style={{ padding: 0 }}>
          {displayPresets ? (
            <>
              {showTextInput && (
                <Box p="8" borderBottom="divider">
                  <TextField
                    aria-label="Type a time range, for example last 7 days"
                    value={typedInput}
                    onChange={(value) => {
                      setTypedInput(value);
                      if (typedInputIsInvalid) {
                        setTypedInputIsInvalid(false);
                      }
                    }}
                    isInvalid={typedInputIsInvalid}
                    style={{ width: '100%' }}
                  >
                    <Input
                      placeholder="e.g. last 7 days"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleTypedInputSubmit();
                        }
                      }}
                    />
                  </TextField>
                </Box>
              )}
              <Menu
                aria-label="Preset date range options"
                selectionMode="single"
                selectedKeys={(() => {
                  if (selectedValue.isLiveMode) {
                    return new Set(['live-mode']);
                  }
                  if (selectedPreset) {
                    return new Set([presetValue(selectedPreset)]);
                  }
                  return new Set<string>();
                })()}
                onAction={(key) => {
                  if (key === 'custom-date-range') {
                    setDisplayPresets(false);
                    return;
                  }
                  if (key === 'live-mode') {
                    handleLiveModeToggle();
                    return;
                  }
                  handleSelectPreset(availablePresets.find((preset) => presetValue(preset) === key));
                }}
                style={{ minWidth: 190 }}
              >
                {showLiveMode && (
                  <MenuSection>
                    <MenuItem id="live-mode" textValue="Live mode">
                      <LiveTag paused={liveModePaused} />
                      Live
                    </MenuItem>
                  </MenuSection>
                )}
                <MenuSection>
                  {availablePresets.map((preset) => {
                    const startDate = presetStartDate(preset);
                    const endDate = moment().toDate();
                    const value = presetValue(preset);
                    return (
                      <MenuItem id={value} key={value}>
                        <DateTag>{getPresetLabel(startDate, endDate)}</DateTag>
                        {presetLabel(preset)}
                      </MenuItem>
                    );
                  })}
                </MenuSection>
                {!noCustom && (
                  <MenuSection>
                    <MenuItem textValue="custom date range" id="custom-date-range">
                      <DateTag>
                        <Icon name="calendar" size="tiny" />
                      </DateTag>
                      Custom range
                    </MenuItem>
                  </MenuSection>
                )}
              </Menu>
            </>
          ) : (
            <Box p="12">
              <RangeCalendar onChange={(value) => handleCalendarChange(value)}>
                <header>
                  <IconButton
                    slot="previous"
                    icon="chevron-left"
                    aria-label="previous"
                    size="small"
                    variant="minimal"
                  />
                  <Heading />
                  <IconButton slot="next" icon="chevron-right" aria-label="next" size="small" variant="minimal" />
                </header>
                <CalendarGrid>{(date) => <CalendarCell date={date} />}</CalendarGrid>
              </RangeCalendar>
            </Box>
          )}
        </Dialog>
      </Popover>
    </AriaDateRangePicker>
  );
};

const DateTag: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <Box backgroundColor="secondaryHover" borderRadius="3" py="6" textAlign="center" style={{ minWidth: 36 }}>
      <Text size="xSmall">{children}</Text>
    </Box>
  );
};

const LiveTag: React.FC<{ paused: boolean }> = ({ paused }) => {
  return (
    <Box borderRadius="3" py="3" display="flex" alignItems="center" justifyContent="center" style={{ minWidth: 36 }}>
      <LiveModeIndicator paused={paused} />
    </Box>
  );
};
